// Inline "what is this?" help for the core navigation vocabulary. ONE
// source of truth mapping each top-level record/workflow concept (and
// the Opportunities sub-views) to a plain-language definition, shared by
// the top-nav + sidebar `?` affordances (see CoreVocabularyTip.tsx).
//
// Deliberately kept SEPARATE from the Cleanup glossary
// (web/src/cleanup/cleanup-glossary.ts) so the two domains — core
// navigation vs the Cleanup workspace — never entangle. A term that
// belongs to both would be a smell worth resolving, not silently
// duplicating.
//
// Pure + root-safe (imports nothing) so it can be unit-tested in
// isolation and can't drift between the surfaces that render it.
//
// Voice rule (same as the Cleanup glossary): a first-time, non-power
// user — e.g. a Planning policy manager on her first look — must
// understand each term with no other OS knowledge. No jargon defined
// with jargon. Definitions use the approved vocabulary from CLAUDE.md /
// vocabulary-system.md and avoid every banned word: "deal",
// "journey", "lead" (as a noun), unqualified "pipeline", "flow",
// "customer", and unqualified "client" (always qualified by company).

export interface CoreVocabularyEntry {
  term: string;
  // One or two plain sentences. First sentence = what it IS; second
  // (optional) = when you'd use it / what it's for.
  definition: string;
}

export const CORE_VOCABULARY: Readonly<Record<string, CoreVocabularyEntry>> = {
  // ── Record surfaces (top nav) ────────────────────────────────────
  contacts: {
    term: "Contacts",
    definition:
      "Every individual person in the system, each as their own record. Start here to look someone up by name, preferred name, spouse, phone, or email.",
  },
  households: {
    term: "Households",
    definition:
      "The family unit you serve together — usually a couple — grouping the people who share finances. Most work centers on the household, not the single person.",
  },

  // ── Workflow surfaces (top nav) ──────────────────────────────────
  opportunities: {
    term: "Opportunities",
    definition:
      "People you're working to bring on as a client of one of the companies. Each one ends won or lost.",
  },
  processing: {
    term: "Processing",
    definition:
      "Work that's already been agreed to and is now being delivered — a loan heading to closing, a policy being issued. It moves through stages until it's complete or falls through.",
  },
  "ongoing-care": {
    term: "Ongoing Care",
    definition:
      "The recurring, year-after-year service for an existing household — a renewal cycle, an annual review, a birthday touch. It repeats on a schedule instead of ending.",
  },
  forms: {
    term: "Forms",
    definition:
      "Questionnaires and intake forms you build and share as a hosted link — submissions land here, match to the household, and can drive automations (like stopping a renewal chase once the form is in).",
  },

  // ── Opportunities sub-views (the slices Andrea named on her first
  //    look: "forecasting, aging, boards … what those mean") ─────────
  "opportunity-board": {
    term: "Opportunity Board",
    definition:
      "Your open opportunities laid out as cards by stage, so you can see where each one stands and move it forward.",
  },
  "opportunity-aging": {
    term: "Opportunity Aging",
    definition:
      "Open opportunities sorted by how long they've gone without movement, so the ones going stale surface first.",
  },
  "opportunity-forecast": {
    term: "Opportunity Forecast",
    definition:
      "Open opportunities grouped by expected close date and value, to project what's likely to land and when.",
  },

  // ── Ongoing Care sub-views ───────────────────────────────────────
  "plans-by-month": {
    term: "Plans by month",
    definition:
      "Your ongoing-care plans laid out across the year by the month each one is anchored to, so you can see the workload ahead and click a month to work it.",
  },

  // ── Records + reporting / data surfaces (sidebar) ────────────────
  "service-records": {
    term: "Policies & Services",
    definition:
      "The actual products a household holds across companies — a home policy, an auto policy, a refinance, a managed portfolio, an estate plan. These are the in-force products themselves, not work in progress.",
  },
  reports: {
    term: "Reports",
    definition:
      "Filterable, exportable lists that answer questions across your records, like active households aged 55–65. Filter on screen, then export to Excel in one click.",
  },
  "data-needed": {
    term: "Data Needed",
    definition:
      "Your short personal list of judgment calls only you can make — setting a fit assessment, an expected close date, a borrower tier.",
  },
  cleanup: {
    term: "Cleanup",
    definition:
      "One place to find and fix data problems — possible duplicates, records missing info, unmatched payments — each handled the same way, with an undo.",
  },
  "system-jobs": {
    term: "What Runs Automatically",
    definition:
      "Every scheduled job that fires on its own, described in plain English — backups, syncs, scoring, and scheduled business actions. Rules you build and own live in Automations instead.",
  },
} as const;

// Lookup used by the inline affordance. Case-insensitive + trimmed on
// the key; returns null for unknown terms so the caller can render
// nothing (no empty bubble on a typo).
export function lookupCoreVocabulary(key: string): CoreVocabularyEntry | null {
  return CORE_VOCABULARY[key.trim().toLowerCase()] ?? null;
}

// Stable ordering (insertion order) exposed explicitly so a help page /
// test can agree with the surfaces that render these terms.
export const CORE_VOCABULARY_ORDER: readonly string[] =
  Object.keys(CORE_VOCABULARY);
